/**
 * The active company filter, as a row of removable chips.
 *
 * Twelve selects that all say "any" until one of them does not is a filter you
 * have to audit rather than read, and both screens that carry this filter hit
 * the same wall: the register list page and the campaign audience builder. The
 * list page grew its own inline copy of this first; it now lives here so the two
 * cannot describe the same filter differently. A chip that says "profitable" on
 * one screen and "profitable=true" on the other is how somebody concludes they
 * are looking at two different filters.
 *
 * Pure module - no React. The translator is passed in rather than imported so
 * this can be tested with an identity function and so it carries no opinion
 * about which app's i18n context it runs under.
 *
 * Labels come from `ariregister.filter.*` for the same reason the form does: if
 * one county selects 12 000 rows on one screen it has to mean the same 12 000 on
 * the other, and shared wording is the cheapest guarantee of that.
 */
import {
  ANY,
  EMPTY_AUDIENCE_DRAFT,
  registeredRange,
  type AudienceDraft,
  type AudienceFilter,
} from "./audienceFilter";

export type Translate = (key: string, vars?: Record<string, string | number>) => string;

export interface AudienceChip {
  /** Stable react key, and the id a caller can use to style one chip specially. */
  key: string;
  label: string;
  /** The filter patch that removes exactly this chip and nothing else. */
  clear: Partial<AudienceFilter>;
}

/** An open-ended range as one readable string. The infinity sign rather than an
 *  empty side, because "1000-" reads as a truncated number. */
function range(min: string, max: string): string {
  return `${min || "0"}-${max || "∞"}`;
}

/** Every set filter, in the order the form presents them. */
export function audienceChips(f: AudienceFilter, t: Translate): AudienceChip[] {
  const out: AudienceChip[] = [];
  const push = (key: string, label: string, clear: Partial<AudienceFilter>) => out.push({ key, label, clear });

  // Named rather than a bare quoted string: since 2026-08-08 the same box also
  // matches a contact e-mail or phone, so a lone "+372 5551234" chip reads like
  // a stray value rather than a search that is narrowing the list.
  if (f.q) push("q", `${t("ariregister.filter.search")}: "${f.q}"`, { q: "" });
  if (f.county !== ANY) push("county", f.county, { county: ANY });
  // The readable activity name needs the EMTAK table, which the caller has and
  // this module does not. The raw code is a usable fallback, never a blank chip.
  if (f.emtak) push("emtak", `${t("ariregister.filter.activity")}: ${f.emtak}`, { emtak: "" });
  if (f.status !== ANY) push("status", t(`ariregister.status.${f.status}`), { status: ANY });
  if (f.legalForm !== ANY) push("legalForm", f.legalForm, { legalForm: ANY });
  if (f.hasVat !== ANY) {
    push("hasVat", t(f.hasVat === "true" ? "ariregister.filter.vatYes" : "ariregister.filter.vatNo"), { hasVat: ANY });
  }
  if (f.hasWebsite !== ANY) {
    push(
      "hasWebsite",
      t(f.hasWebsite === "true" ? "ariregister.filter.websiteYes" : "ariregister.filter.websiteNo"),
      { hasWebsite: ANY },
    );
  }
  if (f.hasEmail) push("hasEmail", t("ariregister.filter.hasEmail"), { hasEmail: false });
  if (f.hasPhone) push("hasPhone", t("ariregister.filter.hasPhone"), { hasPhone: false });

  if (f.revenueMin || f.revenueMax) {
    push("revenue", `${t("ariregister.filter.revenue")}: ${range(f.revenueMin, f.revenueMax)} €`, {
      revenueMin: "",
      revenueMax: "",
    });
  }
  if (f.employeesMin || f.employeesMax) {
    push("employees", `${t("ariregister.filter.employees")}: ${range(f.employeesMin, f.employeesMax)}`, {
      employeesMin: "",
      employeesMax: "",
    });
  }
  if (f.capitalMin || f.capitalMax) {
    push("capital", `${t("ariregister.filter.capital")}: ${range(f.capitalMin, f.capitalMax)} €`, {
      capitalMin: "",
      capitalMax: "",
    });
  }
  if (f.webNeedMin || f.webNeedMax) {
    push("webNeed", `${t("ariregister.filter.webNeed")}: ${range(f.webNeedMin, f.webNeedMax)}`, {
      webNeedMin: "",
      webNeedMax: "",
    });
  }
  if (f.hasDebt !== ANY) {
    push("hasDebt", t(f.hasDebt === "true" ? "ariregister.filter.hasDebtYes" : "ariregister.filter.hasDebtNo"), {
      hasDebt: ANY,
    });
  }
  if (f.debtMin) {
    push("debtMin", `${t("ariregister.filter.debt")}: ${range(f.debtMin, "")} €`, { debtMin: "" });
  }
  if (f.hasLicence !== ANY) {
    push(
      "hasLicence",
      t(f.hasLicence === "true" ? "ariregister.filter.hasLicenceYes" : "ariregister.filter.hasLicenceNo"),
      { hasLicence: ANY },
    );
  }
  if (f.licenceSince) {
    push(
      "licenceSince",
      `${t("ariregister.filter.licenceSince")}: ${new Date(f.licenceSince).toLocaleDateString("et-EE")}`,
      { licenceSince: null },
    );
  }
  if (f.wonProcurement !== ANY) {
    push(
      "wonProcurement",
      t(f.wonProcurement === "true" ? "ariregister.filter.wonProcurementYes" : "ariregister.filter.wonProcurementNo"),
      { wonProcurement: ANY },
    );
  }
  if (f.procurementSince) {
    push(
      "procurementSince",
      `${t("ariregister.filter.procurementSince")}: ${new Date(f.procurementSince).toLocaleDateString("et-EE")}`,
      { procurementSince: null },
    );
  }
  if (f.profitable !== ANY) {
    push(
      "profitable",
      t(f.profitable === "true" ? "ariregister.filter.profitableYes" : "ariregister.filter.profitableNo"),
      { profitable: ANY },
    );
  }

  const reg = registeredRange(f);
  if (reg) {
    const fmt = (d: Date) => d.toLocaleDateString("et-EE");
    push("registered", `${t("ariregister.filter.registered")}: ${fmt(reg.from)}-${fmt(reg.to)}`, {
      registeredFrom: null,
      registeredTo: null,
    });
  }

  return out;
}

const DRAFT_KEYS = Object.keys(EMPTY_AUDIENCE_DRAFT) as (keyof AudienceDraft)[];

/**
 * The typing-buffer half of removing a chip.
 *
 * Four of the fields are debounced through a draft (search, capital, revenue,
 * employees). Clearing only the committed filter looks right for 400ms and then
 * the pending draft commits the value straight back - so a chip removal that
 * touches one of those keys has to blank the draft in the same breath.
 */
export function draftPatchFor(clear: Partial<AudienceFilter>): Partial<AudienceDraft> {
  const out: Partial<AudienceDraft> = {};
  for (const k of DRAFT_KEYS) {
    if (k in clear) out[k] = "";
  }
  return out;
}
